import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { invoiceService } from '../services/invoiceService';
import { Invoice } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import { Permission } from '../utils/permissions';
import { useDateTime } from '../hooks/useDateTime';
import './InvoiceList.css';

const InvoiceList: React.FC = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const isAdmin = can(Permission.MANAGE_BILLING);
  const { formatCalendarDate } = useDateTime();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; invoice: Invoice | null }>({ open: false, invoice: null });
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderDialog, setReminderDialog] = useState<{ open: boolean; invoice: Invoice | null }>({ open: false, invoice: null });
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  const fetchInvoices = useCallback(async (q?: string) => {
    try {
      setLoading(true);
      setError('');
      const data = await invoiceService.getInvoices(q);
      setInvoices(data);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    const t = setTimeout(() => fetchInvoices(search.trim() || undefined), 300);
    return () => clearTimeout(t);
  }, [search, fetchInvoices]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const formatCurrency = (amount: number, currency = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

  const formatDate = (dateString: string) => formatCalendarDate(dateString);

  const isOverdue = (invoice: Invoice) => !invoice.isPaid && new Date(invoice.dueDate) < new Date();

  const openView = (invoice: Invoice) => {
    window.open(`/invoice/${invoice._id}`, '_blank');
  };

  const confirmPayment = async () => {
    if (!selectedInvoice) return;
    try {
      await invoiceService.markAsPaid(selectedInvoice._id, 'Manual');
      setSuccess('Invoice marked as paid');
      setShowPaymentDialog(false);
      setSelectedInvoice(null);
      fetchInvoices(search.trim() || undefined);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to mark invoice as paid');
    }
  };

  const sendManualReminder = async () => {
    if (!reminderDialog.invoice) return;
    setSendingReminder(true);
    setError('');
    try {
      await invoiceService.sendReminder(reminderDialog.invoice._id);
      setSuccess('Reminder sent');
      setReminderDialog({ open: false, invoice: null });
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  const confirmDeleteInvoice = async () => {
    if (!deleteDialog.invoice) return;
    setDeleting(true);
    try {
      await invoiceService.deleteInvoice(deleteDialog.invoice._id);
      setDeleteDialog({ open: false, invoice: null });
      await fetchInvoices(search.trim() || undefined);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to delete invoice');
    } finally {
      setDeleting(false);
    }
  };

  const unpaidCount = invoices.filter((invoice) => !invoice.isPaid).length;

  const clientBody = (row: Invoice) => (
    <div className="invoice-client">
      <span className="invoice-client-name">{row.clientCompanyName || row.clientName}</span>
      <span className="invoice-client-email">{row.clientEmail}</span>
    </div>
  );

  const amountBody = (row: Invoice) => (
    <span className="invoice-amount">{formatCurrency(row.amount, row.currency || 'USD')}</span>
  );

  const dueDateBody = (row: Invoice) => (
    <span className={isOverdue(row) ? 'invoice-due overdue' : 'invoice-due'}>
      {formatDate(row.dueDate)}
    </span>
  );

  const statusBody = (row: Invoice) => (
    <span className={`invoice-status ${row.isPaid ? 'paid' : isOverdue(row) ? 'overdue' : 'unpaid'}`}>
      {row.isPaid ? 'Paid' : isOverdue(row) ? 'Overdue' : 'Unpaid'}
    </span>
  );

  const actionBody = (row: Invoice) => (
    <div className="invoice-actions">
      <button type="button" className="inv-icon-btn" title="View" onClick={() => openView(row)}>
        <i className="pi pi-eye" />
      </button>
      {isAdmin && !row.isPaid && (
        <button
          type="button"
          className="inv-icon-btn success"
          title="Mark as paid"
          onClick={() => {
            setSelectedInvoice(row);
            setShowPaymentDialog(true);
          }}
        >
          <i className="pi pi-check" />
        </button>
      )}
      {isAdmin && !row.isPaid && (
        <button
          type="button"
          className="inv-icon-btn warn"
          title="Send reminder"
          onClick={() => setReminderDialog({ open: true, invoice: row })}
        >
          <i className="pi pi-send" />
        </button>
      )}
      {isAdmin && (
        <button
          type="button"
          className="inv-icon-btn danger"
          title="Delete"
          onClick={() => setDeleteDialog({ open: true, invoice: row })}
        >
          <i className="pi pi-trash" />
        </button>
      )}
    </div>
  );

  return (
    <div className="invoice-list-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">{isAdmin ? 'Invoices' : 'My invoices'}</h1>
          <p className="page-subtitle">
            {invoices.length} total{unpaidCount ? ` · ${unpaidCount} unpaid` : ''}
          </p>
        </div>
        <div className="page-header-actions">
          <span className="p-input-icon-left invoice-search">
            <i className="pi pi-search" />
            <InputText
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search invoices"
            />
          </span>
          {isAdmin && (
            <Button label="New invoice" icon="pi pi-plus" className="new-ticket-btn" onClick={() => navigate('/invoices/new')} />
          )}
        </div>
      </div>

      {error && <Message severity="error" text={error} className="invoice-banner" />}
      {success && <Message severity="success" text={success} className="invoice-banner" />}

      {isMobile ? (
        <div className="mobile-cards-container">
          {invoices.map((invoice) => (
            <div key={invoice._id} className="invoice-card">
              <div className="card-header">
                <div>
                  <div className="invoice-number">{invoice.invoiceNumberStr}</div>
                  <div className="invoice-client-name">{invoice.clientCompanyName || invoice.clientName}</div>
                </div>
                {statusBody(invoice)}
              </div>
              <div className="card-meta">
                <span>{formatCurrency(invoice.amount, invoice.currency || 'USD')}</span>
                <span className={isOverdue(invoice) ? 'invoice-due overdue' : 'invoice-due'}>
                  Due {formatDate(invoice.dueDate)}
                </span>
              </div>
              {actionBody(invoice)}
            </div>
          ))}
          {!loading && invoices.length === 0 && (
            <div className="mobile-empty-state">
              <i className="pi pi-file" />
              <h3>No invoices</h3>
              <p>Nothing matches this search.</p>
            </div>
          )}
        </div>
      ) : (
        <Card className="table-card invoices-card">
          <DataTable
            value={invoices}
            loading={loading}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50]}
            emptyMessage="No invoices"
            className="invoices-table"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
          >
            <Column field="invoiceNumberStr" header="Invoice" style={{ width: '8.5rem' }} body={(row: Invoice) => (
              <span className="invoice-id">{row.invoiceNumberStr}</span>
            )} />
            <Column header="Client" body={clientBody} />
            <Column header="Amount" body={amountBody} headerClassName="amount-col" bodyClassName="amount-col" style={{ width: '8.5rem' }} />
            <Column header="Due date" body={dueDateBody} style={{ width: '9rem' }} />
            <Column header="Status" body={statusBody} style={{ width: '7.5rem' }} />
            <Column header="" body={actionBody} style={{ width: isAdmin ? '9.5rem' : '3.5rem' }} />
          </DataTable>
        </Card>
      )}

      <Dialog header="Mark as paid" visible={showPaymentDialog} style={{ width: '25rem' }} onHide={() => setShowPaymentDialog(false)}>
        <p>Mark this invoice as paid?</p>
        <div className="dialog-actions">
          <Button label="Cancel" severity="secondary" onClick={() => setShowPaymentDialog(false)} />
          <Button label="Confirm" onClick={confirmPayment} />
        </div>
      </Dialog>

      <Dialog header="Send payment reminder" visible={reminderDialog.open} onHide={() => setReminderDialog({ open: false, invoice: null })} style={{ width: '28rem' }}>
        {reminderDialog.invoice && (
          <p>Send a reminder for {reminderDialog.invoice.invoiceNumberStr} to {reminderDialog.invoice.clientName}?</p>
        )}
        <div className="dialog-actions">
          <Button label="Cancel" severity="secondary" outlined onClick={() => setReminderDialog({ open: false, invoice: null })} />
          <Button label="Send reminder" icon="pi pi-send" loading={sendingReminder} onClick={sendManualReminder} />
        </div>
      </Dialog>

      <Dialog header="Delete invoice" visible={deleteDialog.open} style={{ width: '28rem' }} onHide={() => setDeleteDialog({ open: false, invoice: null })}>
        <p>Delete this invoice?</p>
        <div className="dialog-actions">
          <Button label="Cancel" severity="secondary" onClick={() => setDeleteDialog({ open: false, invoice: null })} />
          <Button label="Delete" icon="pi pi-trash" severity="danger" loading={deleting} onClick={confirmDeleteInvoice} />
        </div>
      </Dialog>
    </div>
  );
};

export default InvoiceList;
